import { useRef, useState, useEffect, useContext } from "react";
import styles from "./pop.module.css";
import { ThemeContext } from "../../context/ThemeContext";

export default function Popup(props) {
  const inputRef = useRef();
  const reAuthPassword = useRef();
  const fileInputRef = useRef();

  const isFeature = props.valueType === "Enter New Feature";
  const isCategory = props.valueType === "Enter New Category";
  const fields = props.category;
  const [added, setAdded] = useState([]);
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const { isDarkMode } = useContext(ThemeContext);
  const [theme, setTheme] = useState(isDarkMode);

  useEffect(() => {
    setTheme(isDarkMode);

    // Reset selected images when switching away from Image category
    if (props.selectedCategory !== "Image") {
      setImages([]);
    }
  }, [props.selectedCategory, isDarkMode]);

  // ---------------------------
  // Add text value (titles / desc / price)
  // ---------------------------
  function getInputValue() {
    if (!inputRef.current) return;

    let inputValue = inputRef.current.value;
    if (!inputValue) return;

    setAdded((prev) => [...prev, inputValue]);
    inputRef.current.value = "";
  }

  function handleAdd(fieldName) {
    if (!added.includes(fieldName)) {
      setAdded((prev) => [...prev, fieldName]);
    }
  }

  function handleCancel() {
    props.setIsPopupVisible(false);
  }

  // ---------------------------
  // Handle file select (do NOT upload here)
  // ---------------------------
  function handleImageChange(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newImages = [];
    for (let i = 0; i < files.length; i++) {
      newImages.push(files[i]); // Keep File objects
    }

    setImages((prev) => [...prev, ...newImages]);
  }

  // ---------------------------
  // Submit for both feature types
  // ---------------------------
  async function handleSubmit() {
    // IMAGE CATEGORY → return files instead of Firebase URLs
    if (props.selectedCategory === "Image" && images.length > 0) {
      props.getValue(images);  // <-- Return File[] to Form1 (Form1 uploads to API)
      props.setIsPopupVisible(false);
      return;
    }

    // TEXT / CATEGORY
    if (added.length > 0) {
      props.getValue(added);
      props.setIsPopupVisible(false);
      return;
    }
  }

  const order = ["Title", "Image", "Description", "Price"];
  const backgroundColorDarkMode = "#141414";
  const backgroundColorLightMode = "white";

  return (
    <div
      className={styles["backdrop"]}
      onClick={() => props.setIsPopupVisible(false)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => e.preventDefault()}
        className={styles.popup}
        style={{
          background: theme ? backgroundColorDarkMode : backgroundColorLightMode,
          color: theme ? "white" : "black",
        }}
      >
        <p>
          {isCategory ? "Select Categories" : `Select ${props.selectedCategory}s`}
        </p>

        {/* CATEGORY SELECTION */}
        {isCategory && (
          <div className={styles.scrollableContainer}>
            {fields
              .sort((a, b) => order.indexOf(a) - order.indexOf(b))
              .map((fieldName) => (
                <div
                  className={styles.col}
                  key={fieldName}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <label style={{ width: "120px" }}>{fieldName}</label>
                  <button
                    type="button"
                    onClick={() => handleAdd(fieldName)}
                    style={{
                      width: "80px",
                      marginLeft: "10px",
                      backgroundColor: "#008000",
                      height: "35px",
                      padding: "0 12px",
                    }}
                  >
                    Add
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* NON-IMAGE FEATURE FIELD */}
        {props.selectedCategory !== "Image" && isFeature && (
          <>
            {props.selectedCategory === "Price" && (
              <p style={{ margin: "0 0 10px", textAlign: "left" }}>
                Enter the price in your local currency ({props.priceCurrency || "USD"}). Participants will see the price in their local currency.
              </p>
            )}
            <div className={styles.col} style={{ alignItems: "flex-start" }}>
              <input
                ref={inputRef}
                type={props.selectedCategory === "Price" ? "number" : "text"}
                min={props.selectedCategory === "Price" ? "0" : undefined}
                step={props.selectedCategory === "Price" ? "0.01" : undefined}
                placeholder={props.selectedCategory === "Price" ? "Enter amount" : "Enter Here"}
                style={{ backgroundColor: theme ? "black" : "white" }}
              />
              <button
                onClick={getInputValue}
                style={{ backgroundColor: "#008000", marginLeft: "10px", height: "35px", padding: "0 12px" }}
              >
                Add
              </button>
            </div>
          </>
        )}

        {/* RE-AUTH FIELD (UNTOUCHED) */}
        {props.reAuth && (
          <div className={styles.reAuthSection}>
            <h3>Enter Previous Password</h3>
            <input
              type="password"
              ref={reAuthPassword}
              placeholder="Password"
              required
            />
          </div>
        )}

        {/* IMAGE UPLOAD SECTION (returns File[]) */}
        {props.selectedCategory === "Image" && (
          <div
            className={styles.imageSection}
            style={{
              background: theme ? backgroundColorDarkMode : backgroundColorLightMode,
            }}
          >
            <label htmlFor="image-upload" className={styles.imageLabel}>
              Select images:
            </label>
            <input
              ref={fileInputRef}
              type="file"
              id="image-upload"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className={styles.fileInput}
            />
          </div>
        )}

        {/* SHOW SELECTED IMAGES */}
        {images.length > 0 && (
          <div className={styles.added}>
            <h4 style={{ marginRight: "10px" }}>Selected Images:</h4>
            <span>{images.map((img) => img.name).join(", ")}</span>
          </div>
        )}

        {/* SHOW TEXT FEATURES ADDED */}
        {added.length > 0 && (
          <div className={styles.added}>
            <h4 style={{ marginRight: "10px" }}>
              Added {isFeature ? "Feature" : "Categories"}:
            </h4>
            <span>{added.join(", ")}</span>
          </div>
        )}

        <div className={styles["button--container"]}>
          <button type="button" onClick={handleCancel} disabled={isUploading}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              isUploading ||
              (props.selectedCategory === "Image" && images.length === 0)
            }
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
